import { useAsyncData, useState } from '#imports'
import { computed, watch } from 'vue'

type SWRState<T> = {
  data: T | null
  hasFetchedOnce: boolean
}

/**
 * A composable that wraps useAsyncData to provide stale-while-revalidate caching behavior.
 * It maintains a persistent state that survives component remounts and provides the last known data
 * while fetching new data in the background.
 *
 * @param key - Unique key for caching the data
 * @param handler - Async function that fetches the data
 * @param options - Options object that extends useAsyncData options with an optional onDataUpdated callback
 * @returns Object containing the persistent data state and other useAsyncData properties
 */

export function useSWRAsyncData<T>(
  key: string,
  handler: () => Promise<T>,
  options?: Parameters<typeof useAsyncData>[2] & {
    onDataUpdated?: (data: SWRState<T>['data']) => void
  },
) {
  const asyncData = useAsyncData(key, handler, options)

  // Create a persistent state that survives component remounts
  const persistentState = useState<SWRState<T>>(`${key}-swr-state`, () => ({
    data: null,
    hasFetchedOnce: false,
  }))

  // Watch for status changes and sync the persistent state
  watch(
    asyncData.status,
    () => {
      if (
        asyncData.status.value === 'success' ||
        asyncData.status.value === 'error'
      ) {
        persistentState.value = {
          data: asyncData.data.value as T | null,
          hasFetchedOnce: true,
        }
        if (options?.onDataUpdated) {
          options.onDataUpdated(asyncData.data.value as T | null)
        }
      }
    },
    { flush: 'sync' },
  )

  const { data: _data, ...rest } = asyncData

  return {
    data: computed(() => persistentState.value.data),
    ...rest,
    hasFetchedOnce: computed(() => persistentState.value.hasFetchedOnce),
  }
}
